<!--
	Operations — the office's field-work workbench.

	It is where work is authored, sequenced, dispatched and closed (doc/in-progress/
	field-assignment-implementation-plan.md, phase P7): an Assignment is an office
	object before it is ever a technician's day, so the page reads and writes the
	§6 API and nothing else — there is no local mock state here, and no button acts
	without a request behind it.

	The page is one map with three floating surfaces over it:
	  1. the left Details pane — the record the ledger or the map selected, or the
	     form authoring the next piece of work; it opens itself when either
	     begins and collapses to its rail when the operator is done with it;
	  2. the bottom ledger — the district's four working lists as tables
	     (Assignments, Routes, Traps, Technicians), one tab at a time, dragged to
	     the height it is read at;
	  3. the map — the district boundary, the Sites, the trap instances (orange
	     while out, grey once collected), the ring of each standing station around
	     them, one line per route through its sequenced members, the open Work
	     Orders' H3 footprints, and the targets an Assignment is being authored
	     against.

	The page owns the district reads every surface states itself from (users, the
	Site ledger's points, trap configurations, trap stations, assignments,
	routes, trap instances) and the toast every failure path reports through, so a
	read is made once and a refusal is shown in the same place whichever surface
	raised it. A selection is not the page's: it is the shared workbench store,
	which the ledger, the map and the Details pane all read and write, so a map
	click and a ledger row click are the same act. The targets of an authoring
	session are the page's state, because the map picks them and the pane names
	them.
-->
<style scoped>
.topbar-content {
	display: flex;
	align-items: center;
	gap: 10px;
	height: 56px;
	padding: 0 16px;
	background: #fff;
	border-bottom: 1px solid #dee2e6;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

/* The load failure is stated over the map, under the bar, so an empty pane is
   never mistaken for an empty district. */
.load-error {
	position: absolute;
	top: 64px;
	left: 12px;
	right: 12px;
	z-index: 13;
	margin: 0;
}
</style>

<template>
	<FullLayout>
		<template #topbar>
			<div class="topbar-content">
				<i class="bi bi-diagram-3"></i>
				<span class="fw-semibold">Operations Command Center</span>
				<span
					v-if="loading"
					class="spinner-border spinner-border-sm text-secondary"
					role="status"
					aria-label="Loading the district"
				></span>
			</div>
		</template>

		<!-- The page's own reads (technicians, Sites, trap configurations) are
		     what every form needs and none of them may invent; a failure to read
		     one is stated here rather than left to an empty pane. -->
		<div v-if="loadError" class="alert alert-danger load-error" role="alert">
			{{ loadError }}
		</div>

		<OperationsMap
			:assignments="assignments"
			:routes="routes"
			:stations="activeStations"
			:traps="visibleTraps"
			:sites="sites"
			:selection="selection"
			:selected-keys="selectedKeys"
			:selected-work-order-cells="selectedWorkOrderDetail?.cells ?? []"
			:pick-mode="mapPickMode"
			:targets="targets"
			:area-mode="areaMode"
			:area-kind="areaKind"
			:area="store.area"
			:positions="technicianPositions.positions"
			:show-technicians="showTechnicians"
			@select="onMapSelect"
			@clear="onMapClear"
			@pick="addTarget"
			@drawn="addDrawnTarget"
			@area="onAreaSelection"
			@area-cancel="onAreaCancel"
			@select-technician="onTechnicianSelect"
		/>

		<!-- One pane, and it is the page: the record the ledger or the map
		     selected, or the work being authored. Its title says which one, and
		     it opens itself when either begins; collapsing it is the operator's
		     own and leaves the selection where it is. -->
		<ToolRail
			side="left"
			v-model="leftTab"
			:tabs="railTabs"
			topOffset="72px"
			:bottomOffset="railBottomOffset"
			:raised="ledgerExpanded"
		>
			<template #details>
				<OperationsDetail
					:selection="selection"
					:authoring="authoring"
					:users="users"
					:sites="sites"
					:trap-configurations="trapConfigurations"
					:assignments="assignments"
					:routes="routes"
					:stations="stations"
					:traps="traps"
					:work="work"
					:work-detail-row="workDetailRow"
					:selected-keys="selectedKeys"
					:targets="targets"
					:pick-mode="pickMode"
					@select="onDetailSelect"
					@close="closeDetails"
					@notify="showNotification"
					@changed="onChanged"
					@collect="collectTrap"
					@assign-work="assignWork"
					@set-trap="setTrapAtStation"
					@assignment-created="onAssignmentCreated"
					@route-created="onRouteCreated"
					@update:targets="targets = $event"
					@update:pickMode="pickMode = $event"
				/>
			</template>

			<!-- The map's own tools: what a click does, and the area selection
			     that sweeps the tab the ledger has open. Both act on the map, so
			     they are stated here rather than in the details pane, which reads
			     one record at a time. -->
			<template #selection>
				<div class="fw-semibold small mb-1">Click on the map</div>
				<p class="form-text mb-2">
					Selects the trap, assignment, station, route or Work Order footprint
					under the pointer. Ctrl/Meta/Shift adds to the selection instead of
					replacing it; a click on empty map clears it.
				</p>

				<div class="fw-semibold small mb-1">Draw an area</div>
				<p class="small text-muted">
					Selects every {{ areaText.noun }} inside the drawn area, then narrows
					the ledger to them.
				</p>
				<p v-if="areaHint" class="form-text mb-2">{{ areaHint }}</p>
				<div class="d-grid gap-2">
					<button
						class="btn btn-sm"
						:class="
							areaMode === 'rectangle' ? 'btn-primary' : 'btn-outline-primary'
						"
						:disabled="areaDisabled"
						@click="toggleAreaMode('rectangle')"
					>
						<i class="bi bi-bounding-box me-1"></i>Draw rectangle
					</button>
					<button
						class="btn btn-sm"
						:class="
							areaMode === 'polygon' ? 'btn-primary' : 'btn-outline-primary'
						"
						:disabled="areaDisabled"
						@click="toggleAreaMode('polygon')"
					>
						<i class="bi bi-pentagon me-1"></i>Draw polygon
					</button>
				</div>
				<button
					class="btn btn-sm btn-outline-secondary w-100 mt-2"
					:disabled="store.count === 0"
					@click="store.clear()"
				>
					Clear selection
				</button>

				<div class="fw-semibold small mt-3 mb-1">Technician locations</div>
				<div class="form-check mb-2">
					<input
						id="operations-technicians"
						v-model="showTechnicians"
						class="form-check-input"
						type="checkbox"
					/>
					<label class="form-check-label small" for="operations-technicians">
						Show on the map
					</label>
				</div>
				<p class="small text-muted mb-1">
					Avatars show each technician's last known fix. The outline runs green
					(just now) through amber (an hour) to grey (two hours or older).
				</p>
				<p v-if="technicianPositions.error" class="small text-danger mb-0">
					Technician locations are unavailable right now.
				</p>
			</template>
		</ToolRail>

		<!-- The district's four working lists, as tables over the same map: one
		     tab at a time, dragged to the height it is read at through the shared
		     drawer widget, and the pane above ends where it begins. -->
		<OperationsLedger
			:assignments="assignments"
			:routes="routes"
			:work="work"
			:selected-keys="selectedKeys"
			:traps="visibleTraps"
			:users="users"
			:sites="sites"
			:selection="selection"
			:active-key="activeLedger"
			:fraction="ledgerFraction"
			:expanded="ledgerExpanded"
			:area-label="areaLabel"
			:area-keys="store.area ? store.multi : null"
			@update:active-key="activeLedger = $event"
			@select="onLedgerSelect"
			@toggle="onLedgerToggle"
			@new-assignment="newAssignment"
			@new-route="newRoute"
			@update:fraction="ledgerFraction = $event"
			@update:expanded="setLedgerExpanded"
			@clear-area="store.clear()"
		/>

		<ToastNotification
			:title="toastTitle"
			:message="toastMessage"
			:show="toastShow"
			@update:show="toastShow = $event"
		/>
	</FullLayout>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

import { apiClient } from "@/client";
import FullLayout from "@/components/layout/Full.vue";
import type { LedgerRow } from "@/components/operations/ledger";
import OperationsDetail from "@/components/operations/OperationsDetail.vue";
import OperationsLedger from "@/components/operations/OperationsLedger.vue";
import OperationsMap from "@/components/operations/OperationsMap.vue";
import { describeError } from "@/components/operations/wire";
import type {
	AuthoringState,
	MapPickTarget,
	Selection,
	TargetDrawMode,
} from "@/components/operations/model";
import ToolRail from "@/components/panel/ToolRail.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import { useWorkbenchSelection } from "@/map/select";
import type { AreaMode, MapSelectPayload, SelectId } from "@/map/select";
import { useTechnicianPositionStore } from "@/store/technician-position";
import type { GeoJSON } from "geojson";
import type {
	MapReviewQueryEnvelope,
	MapReviewSiteRow,
	MapReviewTrapInstanceRow,
	MapReviewTrapStationRow,
	OperationsWorkRow,
	SyncAssignment,
	SyncRoute,
	TrapConfigurationResult,
	User,
	WorkOrderDetail,
} from "@/type/api";

const toastTitle = ref("");
const toastMessage = ref("");
const toastShow = ref(false);

function showNotification(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

// The district reads every surface states itself from. The Site ledger is read
// as its QUERY projection rather than the resource's list because that projection
// carries each Site's representative point — the vertex a site-targeted
// assignment contributes to its route's line — beside the display address the
// authoring form's select prints.
const users = ref<User[]>([]);
const sites = ref<MapReviewSiteRow[]>([]);
const trapConfigurations = ref<TrapConfigurationResult[]>([]);
const assignments = ref<SyncAssignment[]>([]);
const routes = ref<SyncRoute[]>([]);
const traps = ref<MapReviewTrapInstanceRow[]>([]);
const stations = ref<MapReviewTrapStationRow[]>([]);
const work = ref<OperationsWorkRow[]>([]);

const loading = ref(true);
const loadError = ref("");
const authoring = ref<AuthoringState | null>(null);
const leftTab = ref<string | null>(null);

// The one selection: which row the details pane reads, which rows the ledger has
// checked, and the ring an area selection is in force over. It is a pinia
// singleton, so this workbench claims it on setup — opening Operations never
// inherits what Planning or Map Review had selected.
const store = useWorkbenchSelection();
store.reset();

// The technician overlay: each member's last known Traccar fix, drawn as an
// avatar at the position of the newest one. The store is started when this page
// mounts and stopped when it goes, so a fix stored while nobody is looking at
// Operations costs no read; whether the pins are drawn at all is the operator's
// choice, and defaults on because the map's whole purpose is where people are.
const technicianPositions = useTechnicianPositionStore();
const showTechnicians = ref(true);

/** The store record, narrowed to the numeric row ids this page selects: every
 * object on the map is a server row, so a record naming anything else (an H3
 * cell, say) names nothing the panes can read. */
const selection = computed<Selection | null>(() => {
	const record = store.record;
	if (record === null || typeof record.id !== "number") return null;
	return { kind: record.kind as Selection["kind"], id: record.id };
});

/** The checked rows, as the ledger and the map both read them. */
const selectedKeys = computed(() => store.multi);

// The authoring session's map targets and the mode the map is in. They live on
// the page because two surfaces act on them: the map picks them and the form
// names, removes and submits them. One value each, so a chip and a map click
// cannot disagree about what is being authored.
const targets = ref<MapPickTarget[]>([]);
const pickMode = ref<TargetDrawMode>("pick");

// The map only picks while an Assignment is being authored: a selection's
// details and a Route's form leave the map to its ordinary reading.
const mapPickMode = computed<TargetDrawMode | null>(() =>
	authoring.value !== null && authoring.value.kind !== "route"
		? pickMode.value
		: null,
);

// The ledger tab the operator has open. The page owns it because the map's area
// selection has to know which collection a drawn ring sweeps: the tab names the
// features a ring may select.
const activeLedger = ref("work");

// The armed area tool, if any. Null is the map reading the collection.
const areaMode = ref<AreaMode | null>(null);

/** What a drawn area sweeps, per ledger tab. A tab with no map layer of its own
 * names null, and the tools say so rather than drawing a ring that selects
 * nothing. Work Orders are swept through their footprints; the Work tab lists
 * all three work kinds but only the Work Order footprints are drawn as work. */
const AREA_KIND_BY_TAB: Record<string, string | null> = {
	work: "work_order",
	assignments: "assignment",
	routes: "route",
	traps: "trap",
	technicians: null,
};

const areaKind = computed(() => AREA_KIND_BY_TAB[activeLedger.value] ?? null);

/** The tab's own name, and the noun a row of it is called, for the tools' prose:
 * a Work Order is swept through its footprint, so that tab's noun is the
 * footprint rather than the work item. */
const AREA_TAB_TEXT: Record<string, { title: string; noun: string }> = {
	work: { title: "Work", noun: "Work Order footprint" },
	assignments: { title: "Assignments", noun: "assignment" },
	routes: { title: "Routes", noun: "route" },
	traps: { title: "Traps", noun: "trap" },
	technicians: { title: "Technicians", noun: "technician" },
};

const areaText = computed(
	() => AREA_TAB_TEXT[activeLedger.value] ?? { title: "", noun: "row" },
);

/** Why the area tools cannot be used right now, or null when they can: a tab
 * with nothing drawn has no ring to sweep, and an authoring form already owns
 * the map's gestures. */
const areaHint = computed<string | null>(() => {
	if (authoring.value !== null) {
		return "Finish or close the form first: it owns the map's clicks.";
	}
	if (areaKind.value === null) {
		return `The ${areaText.value.title} tab has nothing drawn to select.`;
	}
	return null;
});

const areaDisabled = computed(() => areaHint.value !== null);

/** The drawn area as the ledger's own clause: named with what it selected, so
 * the chip and the highlighted rows cannot disagree. */
const areaLabel = computed(() =>
	store.area === null ? null : `Drawn area · ${store.count} selected`,
);

// Leaving a tab takes its swept-up selection with it: a ring drawn over
// Assignments must not narrow Routes.
watch(activeLedger, () => {
	areaMode.value = null;
	store.clear();
});

// The Work Order read on demand. A footprint is drawn for every open Work Order,
// while the Work queue lists the work an office dispatches, so a clicked
// footprint can name a row the page has never read; this is what the details
// pane reads for it, and what the map frames.
const selectedWorkOrderDetail = ref<WorkOrderDetail | null>(null);
let workDetailToken = 0;

watch(
	() => store.record,
	async (record) => {
		const token = ++workDetailToken;
		selectedWorkOrderDetail.value = null;
		if (
			record === null ||
			record.kind !== "work_order" ||
			typeof record.id !== "number"
		) {
			return;
		}
		try {
			const detail = await apiClient.JSONGet<WorkOrderDetail>(
				`/api/work-order/${record.id}`,
			);
			if (token === workDetailToken) selectedWorkOrderDetail.value = detail;
		} catch {
			// The queue row (when there is one) still renders; the footprint
			// simply does not frame.
		}
	},
);

/** The fetched work order, as the row the details pane renders: the same shape
 * the Work queue builds, so `WorkDetails` reads one kind of row. */
const workDetailRow = computed<OperationsWorkRow | null>(() => {
	const detail = selectedWorkOrderDetail.value;
	if (detail === null) return null;
	return {
		origin: "work_order",
		id: detail.id,
		title: detail.description,
		state: detail.state,
		priority: detail.priority,
		due_at: detail.due_at,
		target_summary:
			detail.cells.length > 0 ? "H3 footprint" : "no H3 footprint",
	};
});

// The ledger's height, as the fraction of the page it covers — the same reading
// Map Review's ledger drags by. The rail above ends where the ledger begins.
const ledgerFraction = ref(0.34);
const ledgerExpanded = computed(() => ledgerFraction.value >= 0.99);

function setLedgerExpanded(expanded: boolean) {
	ledgerFraction.value = expanded ? 1 : 0.34;
}

// A retired station is out of the trapping network: its ring is not drawn and
// its instances are not listed, because both are offers to visit it. The rows
// stay in `stations`/`traps` so a selection made before a retirement still
// resolves in the Details pane.
const activeStations = computed(() =>
	stations.value.filter((station) => station.active),
);

const retiredStationIds = computed(
	() =>
		new Set(
			stations.value
				.filter((station) => !station.active)
				.map((station) => station.id),
		),
);

// An off-station instance (station_id null) belongs to no station, so no
// retirement can exclude it.
const visibleTraps = computed(() =>
	traps.value.filter(
		(trap) =>
			trap.station_id === null || !retiredStationIds.value.has(trap.station_id),
	),
);

// The pane has two tabs: Details, whose name is its content (a form being
// written says which one, otherwise it is the reader for whatever is selected),
// and the map's own tools.
const railTabs = computed(() => [
	{
		key: "details",
		icon: authoring.value === null ? "bi bi-card-text" : "bi bi-plus-lg",
		title:
			authoring.value === null
				? "Details"
				: authoring.value.kind === "route"
					? "New route"
					: "New assignment",
		bodyWidth: "400px",
	},
	{
		key: "selection",
		icon: "bi bi-tools",
		title: "Map tools",
		bodyWidth: "400px",
	},
]);

// The tab strip is a header's 43px: 6px of padding above and below its 30px
// tab, plus the hairline under it. The pane never grows a negative height when
// the ledger is dragged to the top — it stands as that header alone.
const RAIL_HEADER_HEIGHT = 43;

// The pane ends where the ledger begins, so the two floating surfaces never
// cover one another. The ledger's height is the fraction it is dragged to, so
// the pane follows the drag live instead of stepping between presets.
const railBottomOffset = computed(
	() =>
		`min(calc(${(ledgerFraction.value * 100).toFixed(2)}vh + 12px), ` +
		`calc(100% - ${72 + RAIL_HEADER_HEIGHT}px))`,
);

// A selection or an authoring session is a reason to read the pane, so it opens
// itself; the operator collapses it when done. Closing the authoring session
// takes the picking session with it: a target picked for a form that has gone
// away is not a target for the next one.
watch([selection, authoring], ([nextSelection, nextAuthoring]) => {
	if (nextSelection !== null || nextAuthoring !== null) {
		leftTab.value = "details";
	}
	if (nextAuthoring === null) {
		targets.value = [];
		pickMode.value = "pick";
	}
});

/** A target clicked on the map. The same Site or parcel clicked twice is one
 * target rather than two Assignments for the same place. */
function addTarget(target: MapPickTarget) {
	const duplicate = targets.value.some((existing) => {
		if (existing.kind === "site" && target.kind === "site") {
			return existing.siteId === target.siteId;
		}
		if (existing.kind === "parcel" && target.kind === "parcel") {
			return (
				existing.externalId === target.externalId &&
				existing.region === target.region
			);
		}
		return false;
	});
	if (duplicate) return;
	targets.value = [...targets.value, target];
}

/** A drawn point or area. An Assignment carries one drawn geometry, so at most
 * one is targeted per submit and a second draw replaces the first. */
function addDrawnTarget(geometry: GeoJSON.Point | GeoJSON.Polygon) {
	const drawn: MapPickTarget =
		geometry.type === "Point"
			? { kind: "point", geometry }
			: { kind: "area", geometry };
	targets.value = [
		...targets.value.filter(
			(target) => target.kind !== "point" && target.kind !== "area",
		),
		drawn,
	];
}

let readToken = 0;

// One read of everything the workbench draws. The assignment collection is read
// whole because it is the working set three surfaces state themselves from — the
// technician load, the route lines' vertices, and the details of whatever is
// selected — and a route's members are not otherwise resolvable without a read
// per member.
async function readDistrict() {
	const token = ++readToken;
	const [
		userList,
		sitePage,
		configurations,
		assignmentPage,
		routePage,
		stationPage,
		openTrapPage,
		collectedTrapPage,
	] = await Promise.all([
		apiClient.JSONGet<User[]>("/api/user"),
		apiClient.JSONQueryAll<MapReviewSiteRow>("/api/site", { limit: 500 }),
		apiClient.JSONGet<TrapConfigurationResult[]>("/api/trap-configuration"),
		apiClient.JSONQueryAll<SyncAssignment>("/api/assignment", { limit: 500 }),
		// A cancelled Route is history: dispatching, sequencing and membership
		// are all finished for it, and its discarded work has already returned to
		// the unassigned pool. The workbench's working set is the Routes still in
		// play, so the read excludes cancelled ones rather than drawing lines and
		// ledger rows nothing can act on.
		apiClient.JSONQueryAll<SyncRoute>("/api/route", {
			limit: 200,
			order: [{ property: "window_start", direction: "desc" }],
			filter: [
				{ property: "state", operator: "not-equal", value: "cancelled" },
			],
		}),
		apiClient.JSONQueryAll<MapReviewTrapStationRow>("/api/trap-station", {
			limit: 500,
		}),
		apiClient.JSONQueryAll<MapReviewTrapInstanceRow>("/api/trap-instance", {
			limit: 500,
			filter: [{ property: "state", operator: "equal", value: "set" }],
		}),
		apiClient.JSONQuery<MapReviewQueryEnvelope<MapReviewTrapInstanceRow>>(
			"/api/trap-instance",
			{
				limit: 100,
				order: [{ property: "date", direction: "desc" }],
				filter: [{ property: "state", operator: "equal", value: "collected" }],
			},
		),
	]);
	const workPage = await apiClient.JSONGet<{ work: OperationsWorkRow[] }>(
		"/api/operations/work",
	);
	if (token !== readToken) return;
	users.value = userList ?? [];
	sites.value = sitePage.items;
	trapConfigurations.value = configurations ?? [];
	assignments.value = assignmentPage.items;
	routes.value = routePage.items;
	stations.value = stationPage.items;
	traps.value = [...openTrapPage.items, ...collectedTrapPage.items];
	work.value = workPage.work ?? [];
	const liveKeys = new Set(
		work.value.map((row) => `${workKind(row.origin)}:${row.id}`),
	);
	// A Lead or direct Assignment the queue no longer carries is a name the panes
	// cannot resolve: its row is gone and this read says so, so the selection goes
	// with it rather than pointing the details pane at nothing. A Work Order is
	// not cleaned up here: the map draws every open footprint, and a selected one
	// the queue does not dispatch is read on demand, so its row exists even when
	// this list has none.
	const record = store.record;
	if (
		record !== null &&
		(record.kind === "work_lead" || record.kind === "work_direct") &&
		!liveKeys.has(`${record.kind}:${record.id}`)
	) {
		store.clear();
	}
	// The same reading for Routes. The read drops cancelled ones, so a route
	// selection that no longer resolves is exactly the one just discarded: the
	// pane returns to "nothing selected" instead of naming a record the ledger and
	// the map have both stopped drawing.
	if (
		record !== null &&
		record.kind === "route" &&
		typeof record.id === "number" &&
		!routes.value.some((route) => route.id === record.id)
	) {
		store.clear();
	}
}

function workKind(origin: string): string {
	return origin === "lead"
		? "work_lead"
		: origin === "work_order"
			? "work_order"
			: "work_direct";
}

async function load() {
	loading.value = true;
	loadError.value = "";
	try {
		await readDistrict();
	} catch (e: unknown) {
		loadError.value = describeError(e);
		showNotification("Operations data not read", loadError.value);
	} finally {
		loading.value = false;
	}
}

// Every write re-reads rather than patching a copy: a state move, a membership
// change and a created row all change counts and geometry that more than one
// surface states.
async function reload() {
	try {
		await readDistrict();
		loadError.value = "";
	} catch (e: unknown) {
		loadError.value = describeError(e);
		showNotification("Operations data not read", loadError.value);
	}
}

/** Put a row the server just wrote into the collection that renders it,
 * replacing its previous copy if the read already brought one in. */
function upsert<T extends { id: number }>(rows: T[], row: T): T[] {
	const index = rows.findIndex((existing) => existing.id === row.id);
	if (index === -1) return [row, ...rows];
	const next = rows.slice();
	next[index] = row;
	return next;
}

/** A map click resolved by the shared widget: the object under the pointer, and
 * whether the gesture was Ctrl/Meta/Shift. Choosing another record is a new
 * intent, so it ends any authoring session the form was holding. */
function onMapSelect(payload: MapSelectPayload) {
	authoring.value = null;
	store.select(payload.kind, payload.id, { additive: payload.additive });
}

/** A technician's avatar clicked on the map: the same act as clicking their
 * ledger row, so it goes through the store. The pin already stays silent while a
 * form owns the map's gestures, and the guard is repeated here where the select
 * is made — opening a form and the click that lands on a pin are ordered by
 * nobody, and a click that raced the form must not steal its map. */
function onTechnicianSelect({
	id,
	additive,
}: {
	id: number;
	additive: boolean;
}) {
	if (authoring.value !== null) return;
	store.select("technician", id, { additive });
}

/** The details pane naming another row (a technician's route, a route's stop):
 * the same act as clicking that row, so it goes through the store and ends any
 * authoring session behind it. */
function onDetailSelect(next: Selection) {
	authoring.value = null;
	store.select(next.kind, next.id);
}

/** A click on empty map. It is not a decision to throw away a half-written
 * form, so while one is open it is ignored. */
function onMapClear() {
	if (authoring.value) return;
	store.clear();
}

/** A drawn area selects every object of the active ledger tab inside it. */
function onAreaSelection(payload: {
	kind: string;
	ids: SelectId[];
	ring: [number, number][];
}) {
	store.setArea(
		payload.ring,
		payload.ids.map((id) => `${payload.kind}:${id}`),
	);
	if (payload.ids.length > 0) {
		// The pane reads one object, so the record follows the first row swept
		// up. The select is additive precisely so it does not replace the set the
		// ring just wrote; the record is the only thing it changes.
		store.select(payload.kind, payload.ids[0], { additive: true });
	}
	areaMode.value = null;
	leftTab.value = "details";
}

/** The tool was disarmed without a commit (Escape, or a tab change). */
function onAreaCancel() {
	areaMode.value = null;
}

/** Arm the tool, or disarm it when it is already the armed one. */
function toggleAreaMode(mode: AreaMode) {
	areaMode.value = areaMode.value === mode ? null : mode;
}

/** Opening an authoring form starts a fresh picking session: the targets and
 * the mode belong to the form about to open, never to the one it replaces, and
 * an armed area tool is disarmed — the map answers one gesture at a time. */
function beginAuthoring(state: AuthoringState) {
	targets.value = [];
	pickMode.value = "pick";
	areaMode.value = null;
	authoring.value = state;
}

function newAssignment() {
	store.clear();
	beginAuthoring({ kind: "assignment" });
}

function newRoute() {
	store.clear();
	beginAuthoring({ kind: "route" });
}

// A trap's details' one action: open the authoring form
// pre-filled from the instance. The instance keeps sitting there until something
// collects it — opening a form creates nothing (decision 6).
function collectTrap(trap: MapReviewTrapInstanceRow) {
	beginAuthoring({ kind: "collect", trap });
}

function assignWork(technicianId: number) {
	beginAuthoring({ kind: "assignment", technicianId });
}

// A station's details' one action. The work is placed at the Site the station
// stands on, because a Site — not a station — is what an Assignment targets;
// the station only supplied the place the operator started from.
function setTrapAtStation(siteId: number) {
	beginAuthoring({ kind: "set_trap", siteId });
}

/** A ledger row carries what clicking it selects; the store is what that means,
 * so a table row and a map marker land in the pane identically. */
function onLedgerSelect(row: LedgerRow, additive: boolean) {
	if (additive) {
		onLedgerToggle(row);
		return;
	}
	authoring.value = null;
	store.select(row.kind, row.id);
}

/** The ledger's checkbox. A row that was just checked is the one the pane reads,
 * so the record follows it; the select is additive, which leaves the set the
 * checkbox wrote exactly as it is. Unchecking the last row clears the record with
 * the set, and the pane's own "nothing selected" reading takes over. */
function onLedgerToggle(row: LedgerRow) {
	const wasChecked = store.has(row.kind, row.id);
	store.toggle(row.kind, row.id);
	if (!wasChecked) store.select(row.kind, row.id, { additive: true });
}

// Closing the form first; when a selection sits behind it, the pane returns to
// that selection rather than to nothing. With nothing left to read, the pane
// itself goes away and any armed area tool with it.
function closeDetails() {
	if (authoring.value) {
		authoring.value = null;
		return;
	}
	store.clear();
	leftTab.value = null;
	areaMode.value = null;
}

async function onChanged() {
	await reload();
}

// A create answers with the row it wrote, so that row is shown in the same tick
// the form closes: the assignment appears in the ledger, on the map and in the
// pane before any read comes back, and the pane is never left empty while a
// request is in flight. The district read still runs afterwards — the Route a
// composed Assignment joined, and anything else a surface derives, must
// reconcile — but nothing waits on it.
function onAssignmentCreated(created: SyncAssignment) {
	assignments.value = upsert(assignments.value, created);
	authoring.value = null;
	store.select("assignment", created.id);
	void reload();
}

function onRouteCreated(created: SyncRoute) {
	routes.value = upsert(routes.value, created);
	authoring.value = null;
	store.select("route", created.id);
	void reload();
}

onMounted(load);

// The overlay's stream is followed only while this page is open: an accepted fix
// is a frequent event, and a subscription that outlived the page would re-read the
// roster for a surface nobody is looking at. `start` also reads once, so the pins
// are there before the first event arrives; `stop` leaves the rows in place for
// the next visit.
onMounted(() => {
	technicianPositions.start();
});

onBeforeUnmount(() => technicianPositions.stop());
</script>
